import { InspectionFormData, PendingMediaItem } from '@/types/inspection-form.js';

export class OfflineSaveError extends Error {
  readonly isOfflineSave = true;
  constructor() {
    super('Saved offline');
  }
}

export interface StoredMediaItem {
  id: string;
  file: Blob;
  name: string;
  mimeType: string;
  mediaType: 'photo' | 'video';
  label?: string;
  durationSec?: number;
  gpsTagged?: boolean;
  fieldPath: string;
}

interface DraftMediaEntry {
  draftKey: string;
  items: StoredMediaItem[];
  savedAt: number;
}

const DB_NAME = 'boundr-drafts';
const STORE_NAME = 'pending_media';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE_NAME, { keyPath: 'draftKey' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function saveMediaForDraft(draftKey: string, items: StoredMediaItem[]): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const entry: DraftMediaEntry = { draftKey, items, savedAt: Date.now() };
    const req = tx.objectStore(STORE_NAME).put(entry);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function loadMediaForDraft(draftKey: string): Promise<StoredMediaItem[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).get(draftKey);
    req.onsuccess = () => resolve((req.result as DraftMediaEntry | undefined)?.items ?? []);
    req.onerror = () => reject(req.error);
  });
}

export async function deleteMediaForDraft(draftKey: string): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const req = tx.objectStore(STORE_NAME).delete(draftKey);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export function extractPendingFiles(formData: InspectionFormData): StoredMediaItem[] {
  const items: StoredMediaItem[] = [];

  function addMedia(media: PendingMediaItem[], fieldPath: string) {
    for (const item of media) {
      if (item.file) {
        items.push({
          id: item.id,
          file: item.file,
          name: item.file.name,
          mimeType: item.file.type,
          mediaType: item.type,
          label: item.label,
          durationSec: item.durationSec,
          gpsTagged: item.gpsTagged,
          fieldPath,
        });
      }
    }
  }

  addMedia(formData.mainAccessPoint, 'mainAccessPoint');
  addMedia(formData.siteOverview, 'siteOverview');
  addMedia(formData.drainStatus.media, 'drainStatus');
  addMedia(formData.ownershipNotice.media, 'ownershipNotice');
  addMedia(formData.wasteDumping.media, 'wasteDumping');
  addMedia(formData.vegetationStatus.media, 'vegetationStatus');
  addMedia(formData.waterloggedStatus.media, 'waterloggedStatus');
  addMedia(formData.constructionActivity.media, 'constructionActivity');

  return items;
}

export function restoreMediaIntoFormData(
  formData: InspectionFormData,
  storedItems: StoredMediaItem[],
): InspectionFormData {
  const byField = new Map<string, StoredMediaItem[]>();
  for (const item of storedItems) {
    const arr = byField.get(item.fieldPath) ?? [];
    arr.push(item);
    byField.set(item.fieldPath, arr);
  }

  function toMediaItem(s: StoredMediaItem): PendingMediaItem {
    return {
      id: s.id,
      type: s.mediaType,
      label: s.label,
      file: s.file as File,
      url: URL.createObjectURL(s.file),
      durationSec: s.durationSec,
      gpsTagged: s.gpsTagged,
      uploadStatus: 'pending',
    };
  }

  function restore(fieldPath: string): PendingMediaItem[] {
    return (byField.get(fieldPath) ?? []).map(toMediaItem);
  }

  return {
    ...formData,
    mainAccessPoint: restore('mainAccessPoint'),
    siteOverview: restore('siteOverview'),
    drainStatus: { ...formData.drainStatus, media: restore('drainStatus') },
    ownershipNotice: { ...formData.ownershipNotice, media: restore('ownershipNotice') },
    wasteDumping: { ...formData.wasteDumping, media: restore('wasteDumping') },
    vegetationStatus: { ...formData.vegetationStatus, media: restore('vegetationStatus') },
    waterloggedStatus: { ...formData.waterloggedStatus, media: restore('waterloggedStatus') },
    constructionActivity: {
      ...formData.constructionActivity,
      media: restore('constructionActivity'),
    },
  };
}

export function isNetworkError(error: unknown): boolean {
  return error instanceof TypeError;
}
